import { motion } from "framer-motion";
import { LucideIcon } from "lucide-react";

interface PillarCardProps {
  title: string;
  score: number;
  icon: LucideIcon;
  description: string;
  issues?: number;
  delay?: number;
}

export const PillarCard = ({ title, score, icon: Icon, description, issues, delay = 0 }: PillarCardProps) => {
  const getScoreColorClass = (s: number) => {
    if (s >= 80) return "text-score-excellent";
    if (s >= 60) return "text-score-good";
    if (s >= 40) return "text-score-warning";
    return "text-score-critical";
  };

  const getBarColorClass = (s: number) => {
    if (s >= 80) return "bg-score-excellent";
    if (s >= 60) return "bg-score-good";
    if (s >= 40) return "bg-score-warning";
    return "bg-score-critical";
  };

  const getScoreGrade = (s: number) => {
    if (s >= 90) return "A+";
    if (s >= 80) return "A";
    if (s >= 70) return "B";
    if (s >= 60) return "C";
    if (s >= 50) return "D";
    return "F";
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
      whileHover={{ scale: 1.02, y: -4 }}
      className="group relative overflow-hidden rounded-xl border border-border bg-card p-6 transition-all hover:border-primary/50"
    >
      {/* Gradient overlay on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
      
      {/* Glow effect */}
      <div className="absolute -right-20 -top-20 h-40 w-40 rounded-full bg-primary/5 blur-3xl transition-all group-hover:bg-primary/10" />

      <div className="relative z-10">
        <div className="flex items-start justify-between">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-secondary">
            <Icon className="h-6 w-6 text-primary" />
          </div>
          <div className="text-right">
            <span className={`text-2xl font-display font-bold ${getScoreColorClass(score)}`}>
              {getScoreGrade(score)}
            </span>
            <span className="block text-sm text-muted-foreground">{score}/100</span>
          </div>
        </div>

        <h3 className="mt-4 font-display text-lg font-semibold text-foreground">
          {title}
        </h3>
        <p className="mt-2 text-sm text-muted-foreground line-clamp-2">
          {description}
        </p>

        {issues !== undefined && (
          <div className="mt-4 flex items-center gap-2">
            <span className="inline-flex items-center rounded-full bg-destructive/10 px-2.5 py-0.5 text-xs font-medium text-destructive">
              {issues} issues
            </span>
          </div>
        )}

        {/* Progress bar */}
        <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-secondary">
          <motion.div
            className={`h-full rounded-full ${getBarColorClass(score)}`}
            initial={{ width: 0 }}
            whileInView={{ width: `${score}%` }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: delay + 0.3 }}
          />
        </div>
      </div>
    </motion.div>
  );
};
